/**
 * Validation of a native CMS rich-text value: the HTML string the TinyMCE 6.8.6
 * editor (client/components/TinyEditor.tsx) writes, which the CMS stores
 * verbatim and published content renders as is. No server sanitizer exists, so
 * the draft writer is the only gate and it must decide without rewriting: a
 * value is either accepted exactly as authored or refused.
 *
 * Accepted (the editor's toolbar output): paragraphs, headings, lists (advlist
 * styles, nesting), blockquote, preformatted/codesample blocks
 * (`<pre class="language-x">`), hr, tables (thead/tbody/tfoot/caption/colgroup,
 * merged cells, cell blocks), inline strong/em/s/sub/sup/code/br, `span` with
 * styles (underline, text and highlight colour), alignment/indent styles, links
 * and named anchors. Elements are lower-case; attributes are matched
 * case-insensitively and may be double-quoted, single-quoted or unquoted.
 *
 * Refused: every element or attribute outside that grammar (script, style,
 * iframe, img/media, forms, svg, custom elements, event handlers, class/id on
 * anything but the codesample `pre` and the named anchor, ...), malformed or
 * ambiguous markup (unterminated or unseparated attributes, duplicate
 * attributes, raw `<`/`>`/`&`, comments, unclosed or crossed elements,
 * content-model violations), link URIs whose scheme is not http(s)/mailto/tel/
 * sms/ftp, links that would open a new browsing context with an opener
 * (`rel="opener"`, or a target other than _self/_parent/_top without
 * noopener/noreferrer), and styles that are not plain `property: value`
 * declarations of a standard CSS property (or a `--custom` property) whose
 * value is built from keywords, numbers, colours, strings, safe functions and
 * `var()` (no url(), expression(), image-set(), attr(), env(), unknown
 * properties or functions, escapes that hide such tokens, comments or
 * at-rules). The editor has no `valid_styles` filter, so every ordinary
 * property is accepted, including position, z-index, offsets and variables.
 * `var()` is accepted only as the value of a property that cannot take a
 * resource (CSS_VAR_PROPERTIES); never in an image/mask/filter/cursor/content/
 * list-style/border-image property or in a `--custom` value (see below).
 *
 * Image/media elements are not part of the grammar at all: they would embed an
 * asset URL the writer cannot prove it owns, and a CSS `url()` would do the
 * same, so neither is ever accepted (asset provenance fence).
 *
 * Work is linear in the input: one forward scan, no backtracking quantifiers
 * over unbounded input, element depth bounded by the caller, attribute count
 * bounded here. CSS function nesting and attribute length have no limit of
 * their own (the style scan keeps one depth counter, no recursion): the whole
 * value is already bounded by the scalar string bound upstream (and the
 * field's own min/max characters).
 *
 * Scope bound: this boolean validator cannot see the page's stylesheets, so it
 * never proves what a variable holds. Any resource-bearing use of CSS variables
 * (a background/mask/filter image from a variable, an aliased custom property,
 * a variable URL string) needs the project asset-ownership context and stays
 * refused here until the media/asset provenance work supplies it.
 */

// Named entities the editor's default (`named`) serializer writes besides the
// XML base set (tinymce.js `namedEntities`). Each one stands for one non-ASCII
// character, so a decoded value only needs a placeholder for it.
const TINYMCE_NAMED_ENTITIES =
  "nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr deg plusmn sup2 sup3 acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest Agrave Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave Uacute Ucirc Uuml Yacute THORN szlig agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave iacute icirc iuml eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml yacute thorn yuml fnof Alpha Beta Gamma Delta Epsilon Zeta Eta Theta Iota Kappa Lambda Mu Nu Xi Omicron Pi Rho Sigma Tau Upsilon Phi Chi Psi Omega alpha beta gamma delta epsilon zeta eta theta iota kappa lambda mu nu xi omicron pi rho sigmaf sigma tau upsilon phi chi psi omega thetasym upsih piv bull hellip prime Prime oline frasl weierp image real trade alefsym larr uarr rarr darr harr crarr lArr uArr rArr dArr hArr forall part exist empty nabla isin notin ni prod sum minus lowast radic prop infin ang and or cap cup int there4 sim cong asymp ne equiv le ge sub sup nsub sube supe oplus otimes perp sdot lceil rceil lfloor rfloor lang rang loz spades clubs hearts diams OElig oelig Scaron scaron Yuml circ tilde ensp emsp thinsp zwnj zwj lrm rlm ndash mdash lsquo rsquo sbquo ldquo rdquo bdquo dagger Dagger permil lsaquo rsaquo euro";
const BASE_ENTITIES = new Map([
  ["amp", "&"],
  ["lt", "<"],
  ["gt", ">"],
  ["quot", '"'],
  ["apos", "'"],
]);
const NAMED_ENTITIES = new Set(TINYMCE_NAMED_ENTITIES.split(" "));
const ENTITY =
  /&(?:#([0-9]{1,7})|#[xX]([0-9a-fA-F]{1,6})|([A-Za-z][A-Za-z0-9]{0,31}));/y;

/**
 * Decodes character references, or null when one is malformed: a bare `&`,
 * a reference without `;`, an unknown name (e.g. `&colon;`, `&Tab;`) or a code
 * point that is NUL, a surrogate, beyond U+10FFFF or a control other than
 * tab/LF/CR. Text and attribute values are checked against the same decoder,
 * so an entity can never hide a character from the checks that follow.
 */
function decodeEntities(s: string): string | null {
  let out = "";
  let last = 0;
  for (let at = s.indexOf("&"); at >= 0; at = s.indexOf("&", last)) {
    ENTITY.lastIndex = at;
    const m = ENTITY.exec(s);
    if (!m) return null;
    let ch: string;
    if (m[3] !== undefined) {
      const base = BASE_ENTITIES.get(m[3]);
      if (base !== undefined) ch = base;
      else if (NAMED_ENTITIES.has(m[3])) ch = "\ufffd";
      else return null;
    } else {
      const cp = m[1] !== undefined ? parseInt(m[1], 10) : parseInt(m[2], 16);
      if (
        cp === 0 ||
        cp > 0x10ffff ||
        (cp >= 0xd800 && cp <= 0xdfff) ||
        (cp < 32 && cp !== 9 && cp !== 10 && cp !== 13)
      )
        return null;
      ch = String.fromCodePoint(cp);
    }
    out += s.slice(last, at) + ch;
    last = ENTITY.lastIndex;
  }
  return out + s.slice(last);
}

const TEXT_BAD = /[<>\u0000-\u0008\u000b\u000c\u000e-\u001f]/;
const VALUE_BAD = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/;

// ---- URIs ------------------------------------------------------------------

const URI_SCHEMES = new Set(["http", "https", "mailto", "tel", "sms", "ftp"]);
const URI_SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*$/;
const URI_DELIMITER = /[:/?#]/;

/**
 * A link target after entity decoding. Like a browser's URL parser it ignores
 * tab/LF/CR anywhere and spaces at the ends. Anything before the first of
 * `: / ? #` that is followed by `:` must be an allowed scheme, so
 * `javascript:`, `vbscript:`, `data:`, `mhtml:`, `java script:` and every other
 * scheme are refused; paths, queries, fragments and `//host` need no scheme.
 */
function uriSafe(decoded: string) {
  const u = decoded.replace(/[\t\n\r]/g, "");
  let start = 0;
  let end = u.length;
  while (start < end && u.charCodeAt(start) === 32) start++;
  while (end > start && u.charCodeAt(end - 1) === 32) end--;
  const target = u.slice(start, end);
  const at = target.search(URI_DELIMITER);
  if (at < 0 || target[at] !== ":") return true;
  const scheme = target.slice(0, at);
  return URI_SCHEME.test(scheme) && URI_SCHEMES.has(scheme.toLowerCase());
}

// ---- Styles ----------------------------------------------------------------

const CSS_WS = /[ \t\n\r\f]/;
const CSS_HEX = /[0-9a-fA-F]/;
const CSS_PROPERTY = /-?[A-Za-z]+(?:-[A-Za-z]+)*/y;
const CSS_CUSTOM_PROPERTY = /--[A-Za-z0-9_-]+/y;
const CSS_DASHED_IDENT = /--[A-Za-z0-9_-]+/y;
const CSS_VENDOR_PREFIX = /^-(?:webkit|moz|ms|o)-/;
const CSS_HEX_COLOR = /#[0-9A-Fa-f]{3,8}(?![0-9A-Za-z_-])/y;
const CSS_NUMBER =
  /[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?(?:%|[A-Za-z]+)?/y;
const CSS_IDENT = /-?[A-Za-z_][A-Za-z0-9_-]*/y;
const CSS_IMPORTANT = /!\s*important(?![A-Za-z0-9_-])/iy;
const CSS_STRING = /"[^"\\\u0000-\u001f<>]*"|'[^'\\\u0000-\u001f<>]*'/y;
// The functions a value may use: colours, arithmetic, transforms, gradients,
// filters, grid/easing helpers, clip shapes and `var()`. None of them can
// fetch a resource or run script. Everything that can (url(), image-set(),
// image(), element(), cross-fade(), src(), paint(), expression(), attr(),
// env(), ...) is absent, which is what keeps every URL-bearing or executable
// value out of a style, including through a `var()` fallback or a custom
// property value (those are parsed by this same grammar).
const CSS_FUNCTIONS = new Set(
  (
    "var calc min max clamp rgb rgba hsl hsla hwb lab lch oklab oklch color color-mix " +
    "translate translatex translatey translatez translate3d scale scalex scaley scalez scale3d " +
    "rotate rotatex rotatey rotatez rotate3d skew skewx skewy matrix matrix3d perspective " +
    "linear-gradient radial-gradient conic-gradient repeating-linear-gradient " +
    "repeating-radial-gradient repeating-conic-gradient " +
    "blur brightness contrast drop-shadow grayscale hue-rotate invert opacity saturate sepia " +
    "minmax repeat fit-content cubic-bezier steps rect inset circle ellipse polygon"
  ).split(" "),
);
// Standard CSS properties (CSS 2.1 and the CSS3 modules for box, text, font,
// colour, background, border, list, table, flex, grid, multi-column,
// transform, transition, animation, scroll, mask and filter). An unknown name
// (`x`), a legacy script hook (`behavior`, `-moz-binding`) or an `@`-rule is
// not a property and is refused. A `-webkit-`/`-moz-`/`-ms-`/`-o-` prefix on a
// listed name is accepted. Values are checked separately, so listing a
// property that can take an image (background-image, mask, cursor, ...) does
// not admit a url().
const CSS_PROPERTIES = new Set(
  (
    // display, positioning and sizing
    "display position top right bottom left inset inset-block inset-block-start inset-block-end " +
    "inset-inline inset-inline-start inset-inline-end z-index float clear visibility overflow " +
    "overflow-x overflow-y overflow-wrap overflow-anchor box-sizing aspect-ratio object-fit " +
    "object-position clip clip-path isolation contain resize opacity mix-blend-mode zoom " +
    "width height min-width min-height max-width max-height inline-size block-size " +
    "min-inline-size min-block-size max-inline-size max-block-size " +
    // margin and padding
    "margin margin-top margin-right margin-bottom margin-left margin-block margin-block-start " +
    "margin-block-end margin-inline margin-inline-start margin-inline-end " +
    "padding padding-top padding-right padding-bottom padding-left padding-block " +
    "padding-block-start padding-block-end padding-inline padding-inline-start padding-inline-end " +
    // border and outline
    "border border-top border-right border-bottom border-left border-block border-inline " +
    "border-width border-top-width border-right-width border-bottom-width border-left-width " +
    "border-style border-top-style border-right-style border-bottom-style border-left-style " +
    "border-color border-top-color border-right-color border-bottom-color border-left-color " +
    "border-radius border-top-left-radius border-top-right-radius border-bottom-left-radius " +
    "border-bottom-right-radius border-image border-image-source border-image-slice " +
    "border-image-width border-image-outset border-image-repeat " +
    "outline outline-color outline-style outline-width outline-offset box-shadow text-shadow " +
    // background and colour
    "background background-color background-image background-position background-position-x " +
    "background-position-y background-repeat background-size background-attachment " +
    "background-clip background-origin background-blend-mode " +
    "color caret-color accent-color color-scheme " +
    // text
    "text-align text-align-last text-decoration text-decoration-line text-decoration-color " +
    "text-decoration-style text-decoration-thickness text-decoration-skip-ink " +
    "text-underline-offset text-underline-position text-indent text-transform text-overflow " +
    "text-justify text-emphasis text-emphasis-color text-emphasis-style text-emphasis-position " +
    "text-wrap text-rendering text-size-adjust text-fill-color text-stroke text-stroke-color " +
    "text-stroke-width letter-spacing word-spacing word-break word-wrap line-break white-space " +
    "line-height line-clamp vertical-align direction unicode-bidi writing-mode hyphens tab-size " +
    "quotes orphans widows font-smoothing tap-highlight-color box-decoration-break box-orient " +
    // font
    "font font-family font-size font-size-adjust font-style font-weight font-variant " +
    "font-variant-caps font-variant-numeric font-variant-ligatures font-stretch font-kerning " +
    "font-feature-settings font-variation-settings font-synthesis " +
    // list, counter, content and table
    "list-style list-style-type list-style-position list-style-image counter-reset " +
    "counter-increment content border-collapse border-spacing caption-side empty-cells " +
    "table-layout " +
    // flex and grid
    "flex flex-basis flex-direction flex-flow flex-grow flex-shrink flex-wrap order " +
    "align-content align-items align-self justify-content justify-items justify-self " +
    "place-content place-items place-self gap row-gap column-gap grid grid-area " +
    "grid-auto-columns grid-auto-flow grid-auto-rows grid-column grid-column-start " +
    "grid-column-end grid-row grid-row-start grid-row-end grid-template grid-template-areas " +
    "grid-template-columns grid-template-rows " +
    // multi-column
    "columns column-count column-fill column-rule column-rule-color column-rule-style " +
    "column-rule-width column-span column-width " +
    // transform, transition, animation
    "transform transform-origin transform-style transform-box perspective perspective-origin " +
    "backface-visibility translate rotate scale transition transition-delay transition-duration " +
    "transition-property transition-timing-function animation animation-delay " +
    "animation-direction animation-duration animation-fill-mode animation-iteration-count " +
    "animation-name animation-play-state animation-timing-function " +
    // interaction, scroll, effects
    "cursor pointer-events user-select touch-action scroll-behavior scroll-margin " +
    "scroll-padding scroll-snap-type scroll-snap-align scroll-snap-stop will-change filter " +
    "backdrop-filter mask mask-image mask-position mask-repeat mask-size mask-mode mask-origin " +
    "mask-clip mask-composite"
  ).split(" "),
);

// Properties whose value grammar contains no <url>, <image> or other resource
// reference, so a `var()` in them can resolve to anything (including a
// `url(...)` inherited from the surrounding page stylesheet) without a fetch:
// an unusable substitution just makes the declaration invalid at computed-value
// time. Anything not listed (background, background-image, border-image*,
// list-style*, content, cursor, filter, backdrop-filter, mask*, clip-path, any
// future or custom property) gets no `var()`: the validator cannot see the
// value an inherited variable holds, so it only allows it where that value
// cannot matter. Names are matched after case folding and vendor-prefix removal.
const CSS_VAR_PROPERTIES = new Set(
  (
    "display position top right bottom left inset inset-block inset-block-start " +
    "inset-block-end inset-inline inset-inline-start inset-inline-end z-index float clear " +
    "visibility overflow overflow-x overflow-y overflow-wrap overflow-anchor box-sizing " +
    "aspect-ratio object-fit object-position clip isolation contain resize opacity " +
    "mix-blend-mode zoom width height min-width min-height max-width max-height inline-size " +
    "block-size min-inline-size min-block-size max-inline-size max-block-size " +
    "margin margin-top margin-right margin-bottom margin-left margin-block margin-block-start " +
    "margin-block-end margin-inline margin-inline-start margin-inline-end " +
    "padding padding-top padding-right padding-bottom padding-left padding-block " +
    "padding-block-start padding-block-end padding-inline padding-inline-start padding-inline-end " +
    "border border-top border-right border-bottom border-left border-block border-inline " +
    "border-width border-top-width border-right-width border-bottom-width border-left-width " +
    "border-style border-top-style border-right-style border-bottom-style border-left-style " +
    "border-color border-top-color border-right-color border-bottom-color border-left-color " +
    "border-radius border-top-left-radius border-top-right-radius border-bottom-left-radius " +
    "border-bottom-right-radius outline outline-color outline-style outline-width " +
    "outline-offset box-shadow text-shadow background-color background-position " +
    "background-position-x background-position-y background-repeat background-size " +
    "background-attachment background-clip background-origin background-blend-mode " +
    "color caret-color accent-color color-scheme " +
    "text-align text-align-last text-decoration text-decoration-line text-decoration-color " +
    "text-decoration-style text-decoration-thickness text-decoration-skip-ink " +
    "text-underline-offset text-underline-position text-indent text-transform text-overflow " +
    "text-justify text-emphasis text-emphasis-color text-emphasis-style text-emphasis-position " +
    "text-wrap text-rendering text-size-adjust text-fill-color text-stroke text-stroke-color " +
    "text-stroke-width letter-spacing word-spacing word-break word-wrap line-break white-space " +
    "line-height line-clamp vertical-align direction unicode-bidi writing-mode hyphens tab-size " +
    "quotes orphans widows font-smoothing tap-highlight-color box-decoration-break box-orient " +
    "font font-family font-size font-size-adjust font-style font-weight font-variant " +
    "font-variant-caps font-variant-numeric font-variant-ligatures font-stretch font-kerning " +
    "font-feature-settings font-variation-settings font-synthesis " +
    "list-style-type list-style-position counter-reset counter-increment border-collapse " +
    "border-spacing caption-side empty-cells table-layout " +
    "flex flex-basis flex-direction flex-flow flex-grow flex-shrink flex-wrap order " +
    "align-content align-items align-self justify-content justify-items justify-self " +
    "place-content place-items place-self gap row-gap column-gap grid grid-area " +
    "grid-auto-columns grid-auto-flow grid-auto-rows grid-column grid-column-start " +
    "grid-column-end grid-row grid-row-start grid-row-end grid-template grid-template-areas " +
    "grid-template-columns grid-template-rows " +
    "columns column-count column-fill column-rule column-rule-color column-rule-style " +
    "column-rule-width column-span column-width " +
    "transform transform-origin transform-style transform-box perspective perspective-origin " +
    "backface-visibility translate rotate scale transition transition-delay transition-duration " +
    "transition-property transition-timing-function animation animation-delay " +
    "animation-direction animation-duration animation-fill-mode animation-iteration-count " +
    "animation-name animation-play-state animation-timing-function " +
    "pointer-events user-select touch-action scroll-behavior scroll-margin " +
    "scroll-padding scroll-snap-type scroll-snap-align scroll-snap-stop will-change"
  ).split(" "),
);

/** Whether `var()` may appear in the value of this (case-folded) property. */
function cssVarAllowed(name: string) {
  return CSS_VAR_PROPERTIES.has(name.replace(CSS_VENDOR_PREFIX, ""));
}

/**
 * Whether a (case-folded) property name is a standard CSS property, possibly
 * vendor-prefixed, or a `--custom` property.
 */
function cssPropertyAllowed(name: string) {
  if (name.startsWith("--")) return true;
  return CSS_PROPERTIES.has(name.replace(CSS_VENDOR_PREFIX, ""));
}

/**
 * CSS escapes (`\70 `, `\000070`, `\(`) resolved the way a CSS parser does, so
 * an escape cannot hide a token from the grammar below. A backslash before a
 * newline, at the end, or an escape that yields NUL/surrogate/out-of-range is
 * malformed and refused.
 */
function cssUnescape(s: string): string | null {
  if (s.indexOf("\\") < 0) return s;
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c !== "\\") {
      out += c;
      continue;
    }
    const next = s[i + 1];
    if (next === undefined || next === "\n" || next === "\r" || next === "\f")
      return null;
    if (CSS_HEX.test(next)) {
      let j = i + 1;
      while (j < s.length && j < i + 7 && CSS_HEX.test(s[j])) j++;
      const cp = parseInt(s.slice(i + 1, j), 16);
      if (cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff))
        return null;
      out += String.fromCodePoint(cp);
      if (j < s.length && CSS_WS.test(s[j])) {
        if (s[j] === "\r" && s[j + 1] === "\n") j++;
        j++;
      }
      i = j - 1;
    } else {
      out += next;
      i++;
    }
  }
  return out;
}

function matchAt(re: RegExp, s: string, at: number) {
  re.lastIndex = at;
  return re.exec(s);
}

/**
 * A `style` attribute value (already entity-decoded): `property: value;`
 * declarations of a standard or `--custom` property whose values are built only
 * from keywords, numbers with units, percentages, hex colours, quoted strings,
 * the functions above, `,` `/` (and `+ - *` inside a function) and a trailing
 * `!important`. `var()` takes a `--name` and an optional fallback, which is
 * parsed like any other value, and only in a CSS_VAR_PROPERTIES property: the
 * name may be defined or inherited from outside the value, so it is accepted
 * only where any value it holds is fetch-free. A `--custom` declaration is an
 * untyped sink that any property (or `image-set(var(--x))`) may later consume,
 * so its value is literal only: no `var()` (an alias) and no quoted string (a
 * possible image-set URL). One forward pass, one nesting counter, no
 * recursion, so work stays linear in the (already bounded) value.
 */
function styleValid(raw: string) {
  const s = cssUnescape(raw);
  if (s === null || s.indexOf("/*") >= 0) return false;
  let i = 0;
  const skipWs = () => {
    while (i < s.length && CSS_WS.test(s[i])) i++;
  };
  for (;;) {
    skipWs();
    if (i >= s.length) return true;
    if (s[i] === ";") {
      i++;
      continue;
    }
    const custom = matchAt(CSS_CUSTOM_PROPERTY, s, i) !== null;
    let varOk = false;
    if (custom) {
      i = CSS_CUSTOM_PROPERTY.lastIndex;
    } else {
      if (!matchAt(CSS_PROPERTY, s, i)) return false;
      const name = s.slice(i, CSS_PROPERTY.lastIndex).toLowerCase();
      if (!cssPropertyAllowed(name)) return false;
      varOk = cssVarAllowed(name);
      i = CSS_PROPERTY.lastIndex;
    }
    skipWs();
    if (s[i++] !== ":") return false;
    let depth = 0;
    let tokens = 0;
    // 1: just after `var(`, a --name is required; 2: just after it, `)` or `,`.
    let varArg = 0;
    // A bare `(` group is only valid at the start of an operand.
    let groupOk = false;
    for (;;) {
      skipWs();
      if (i >= s.length) break;
      const c = s[i];
      if (varArg === 1) {
        if (!matchAt(CSS_DASHED_IDENT, s, i)) return false;
        i = CSS_DASHED_IDENT.lastIndex;
        varArg = 2;
        groupOk = false;
        tokens++;
        continue;
      }
      if (varArg === 2) {
        if (c !== ")" && c !== ",") return false;
        varArg = 0;
      }
      if (c === ";" && depth === 0) break;
      if (c === ")") {
        if (depth === 0) return false;
        depth--;
        i++;
        groupOk = false;
      } else if (c === "(") {
        if (depth === 0 || !groupOk) return false;
        depth++;
        i++;
      } else if (c === "," || c === "/") {
        i++;
        groupOk = true;
      } else if (c === "!") {
        if (depth !== 0 || !matchAt(CSS_IMPORTANT, s, i)) return false;
        i = CSS_IMPORTANT.lastIndex;
        groupOk = false;
      } else if (matchAt(CSS_HEX_COLOR, s, i)) {
        i = CSS_HEX_COLOR.lastIndex;
        groupOk = false;
      } else if (matchAt(CSS_NUMBER, s, i)) {
        i = CSS_NUMBER.lastIndex;
        groupOk = false;
      } else if (matchAt(CSS_IDENT, s, i)) {
        const name = s.slice(i, CSS_IDENT.lastIndex).toLowerCase();
        i = CSS_IDENT.lastIndex;
        groupOk = false;
        if (s[i] === "(") {
          if (!CSS_FUNCTIONS.has(name) || (name === "var" && !varOk))
            return false;
          depth++;
          i++;
          groupOk = true;
          if (name === "var") varArg = 1;
        }
      } else if (c === '"' || c === "'") {
        if (custom || !matchAt(CSS_STRING, s, i)) return false;
        i = CSS_STRING.lastIndex;
        groupOk = false;
      } else if ((c === "+" || c === "*" || c === "-") && depth > 0) {
        i++;
        groupOk = true;
      } else {
        return false;
      }
      tokens++;
    }
    if (depth !== 0 || varArg !== 0 || tokens === 0) return false;
  }
}

// ---- Link browsing context ---------------------------------------------------

const REL_TOKEN = /^[A-Za-z][A-Za-z0-9-]*$/;
const REL_SPLIT = /[ \t\n\r]+/;
// target keywords that navigate an existing context; any other value is a
// context name that opens a new one when none exists (`_blank` always does).
const TARGET_EXISTING = new Set(["_self", "_parent", "_top"]);

/** Lower-cased rel tokens of a decoded value, or null when empty or not keywords. */
function relTokens(decoded: string): string[] | null {
  const tokens = decoded.split(REL_SPLIT).filter((t) => t !== "");
  return tokens.length > 0 && tokens.every((t) => REL_TOKEN.test(t))
    ? tokens.map((t) => t.toLowerCase())
    : null;
}

/**
 * `rel="opener"` hands the opened page `window.opener`, and so does any link
 * that opens a new context (`_blank`, or a name such as `preview`) without
 * noopener/noreferrer, so a hostile target could navigate the source tab.
 * Checked on the decoded values of both attributes after all attributes are
 * collected: a current-context link (no target, _self/_parent/_top) is fine
 * with any rel, `opener` is never accepted, and every new-context link needs
 * `noopener` or `noreferrer`.
 */
function linkContextSafe(attrs: ReadonlyMap<string, string>) {
  const relValue = attrs.get("rel");
  const rel = relValue === undefined ? [] : relTokens(relValue);
  if (rel === null || rel.includes("opener")) return false;
  const target = attrs.get("target");
  if (target === undefined || TARGET_EXISTING.has(target.toLowerCase()))
    return true;
  return rel.includes("noopener") || rel.includes("noreferrer");
}

// ---- Elements and attributes ----------------------------------------------

type AttrCheck = (decoded: string) => boolean;
const pattern =
  (re: RegExp): AttrCheck =>
  (v) =>
    re.test(v);

const A_STYLE = { style: styleValid };
const A_SPAN = { span: pattern(/^[1-9][0-9]{0,3}$/) };
const A_CELL = {
  ...A_STYLE,
  colspan: pattern(/^[1-9][0-9]{0,3}$/),
  rowspan: pattern(/^[1-9][0-9]{0,3}$/),
  scope: pattern(/^(?:row|col|rowgroup|colgroup)$/i),
};

interface RichTextElement {
  attrs: Record<string, AttrCheck | undefined>;
  /** Allowed child elements; null for a void element. */
  children: ReadonlySet<string> | null;
  /** Whether character data may appear directly inside. */
  text: boolean;
}

const set = (...names: string[]) => new Set(names);
const HEADINGS = ["h1", "h2", "h3", "h4", "h5", "h6"];
const PHRASING = ["strong", "em", "s", "sub", "sup", "code", "span", "a", "br"];
const BLOCK = ["p", ...HEADINGS, "ul", "ol", "blockquote", "pre", "hr", "table"];
const FLOW = [...PHRASING, ...BLOCK];

const el = (
  attrs: RichTextElement["attrs"],
  children: readonly string[] | null,
  text = false,
): RichTextElement => ({
  attrs,
  children: children && new Set(children),
  text,
});

const ELEMENTS = new Map<string, RichTextElement>([
  // Inline marks. TinyMCE writes underline, colours and highlights as `span`.
  ...["strong", "em", "s", "sub", "sup", "code"].map(
    (n) => [n, el({}, PHRASING, true)] as const,
  ),
  ["span", el(A_STYLE, PHRASING, true)],
  ["br", el({}, null)],
  [
    "a",
    el(
      {
        // Link plugin output (+ anchor plugin `id`, validated by its isValidId).
        href: (v) => uriSafe(v),
        title: () => true,
        // target/rel are also checked together once both are known
        // (linkContextSafe), whatever their order, case or quoting.
        target: pattern(/^[A-Za-z_][A-Za-z0-9_-]*$/),
        rel: (v) => relTokens(v) !== null,
        id: pattern(/^[A-Za-z][A-Za-z0-9\-:._]*$/),
      },
      PHRASING,
      true,
    ),
  ],
  // Blocks.
  ...["p", ...HEADINGS].map(
    (n) => [n, el(A_STYLE, PHRASING, true)] as const,
  ),
  ["ul", el(A_STYLE, ["li"])],
  ["ol", el({ ...A_STYLE, start: pattern(/^-?[0-9]{1,9}$/) }, ["li"])],
  ["li", el(A_STYLE, FLOW, true)],
  [
    "blockquote",
    el(A_STYLE, [...HEADINGS, "p", "ul", "ol", "pre", "hr", "table"]),
  ],
  // Codesample plugin: `<pre class="language-x">escaped code</pre>`.
  [
    "pre",
    el(
      { ...A_STYLE, class: pattern(/^language-[A-Za-z0-9_]{1,32}$/) },
      ["code", "br"],
      true,
    ),
  ],
  ["hr", el({}, null)],
  // Tables (table plugin).
  [
    "table",
    el(
      {
        ...A_STYLE,
        border: pattern(/^[0-9]{1,2}$/),
        cellspacing: pattern(/^[0-9]{1,4}$/),
        cellpadding: pattern(/^[0-9]{1,4}$/),
      },
      ["caption", "colgroup", "thead", "tbody", "tfoot"],
    ),
  ],
  ["caption", el(A_STYLE, PHRASING, true)],
  ["colgroup", el({ ...A_STYLE, ...A_SPAN }, ["col"])],
  ["col", el({ ...A_STYLE, ...A_SPAN }, null)],
  ["thead", el(A_STYLE, ["tr"])],
  ["tbody", el(A_STYLE, ["tr"])],
  ["tfoot", el(A_STYLE, ["tr"])],
  ["tr", el(A_STYLE, ["td", "th"])],
  ["td", el(A_CELL, FLOW, true)],
  ["th", el(A_CELL, FLOW, true)],
]);
const ROOT_CHILDREN = set(...BLOCK);

// ---- Scanner ---------------------------------------------------------------

const MAX_ATTRIBUTES = 12;
const TAG_NAME = /[a-z][a-z0-9]*/y;
const ATTR_NAME = /[A-Za-z][A-Za-z0-9-]{0,39}/y;
const isSpace = (code: number) =>
  code === 32 || code === 9 || code === 10 || code === 13 || code === 12;

interface Tag {
  close: boolean;
  name: string;
  attrs: [string, string][];
  selfClose: boolean;
  end: number;
}

/**
 * Reads one tag starting at `<`; null when it is not a well-formed tag:
 * comments, doctype, upper-case or custom-element names, attributes without a
 * separating space, duplicate attributes, unterminated quotes, raw `<`/`>` in a
 * value, or a close tag with anything but its name.
 */
function readTag(s: string, at: number): Tag | null {
  let i = at + 1;
  const close = s[i] === "/";
  if (close) i++;
  if (!matchAt(TAG_NAME, s, i)) return null;
  const name = s.slice(i, TAG_NAME.lastIndex);
  i = TAG_NAME.lastIndex;
  if (close)
    return s[i] === ">"
      ? { close, name, attrs: [], selfClose: false, end: i + 1 }
      : null;
  const attrs: [string, string][] = [];
  for (;;) {
    let j = i;
    while (isSpace(s.charCodeAt(j))) j++;
    const c = s[j];
    if (c === ">")
      return { close, name, attrs, selfClose: false, end: j + 1 };
    if (c === "/")
      return s[j + 1] === ">"
        ? { close, name, attrs, selfClose: true, end: j + 2 }
        : null;
    if (j === i || attrs.length >= MAX_ATTRIBUTES) return null;
    if (!matchAt(ATTR_NAME, s, j)) return null;
    const attr = s.slice(j, ATTR_NAME.lastIndex).toLowerCase();
    if (attrs.some(([n]) => n === attr)) return null;
    j = ATTR_NAME.lastIndex;
    let p = j;
    while (isSpace(s.charCodeAt(p))) p++;
    if (s[p] !== "=") {
      attrs.push([attr, ""]);
      i = j;
      continue;
    }
    p++;
    while (isSpace(s.charCodeAt(p))) p++;
    const q = s[p];
    let value: string;
    if (q === '"' || q === "'") {
      const stop = s.indexOf(q, p + 1);
      if (stop < 0) return null;
      value = s.slice(p + 1, stop);
      i = stop + 1;
    } else {
      let stop = p;
      while (stop < s.length) {
        const ch = s[stop];
        if (isSpace(s.charCodeAt(stop)) || ch === ">") break;
        if (ch === '"' || ch === "'" || ch === "=" || ch === "<" || ch === "`")
          return null;
        stop++;
      }
      if (stop === p) return null;
      value = s.slice(p, stop);
      i = stop;
    }
    if (value.indexOf("<") >= 0 || value.indexOf(">") >= 0) return null;
    attrs.push([attr, value]);
  }
}

function attributesValid(
  tag: string,
  spec: RichTextElement,
  attrs: [string, string][],
) {
  const values = new Map<string, string>();
  for (const [name, raw] of attrs) {
    const check = Object.prototype.hasOwnProperty.call(spec.attrs, name)
      ? spec.attrs[name]
      : undefined;
    if (!check) return false;
    const decoded = decodeEntities(raw);
    if (decoded === null || VALUE_BAD.test(decoded) || !check(decoded))
      return false;
    values.set(name, decoded);
  }
  return tag !== "a" || linkContextSafe(values);
}

/**
 * True when `html` is a native rich-text value (see the file comment).
 * `maxDepth` bounds element nesting.
 */
export function cmsRichTextHtmlValid(html: string, maxDepth: number) {
  const stack: string[] = [];
  let at = 0;
  while (at < html.length) {
    const parent = stack[stack.length - 1] ?? "";
    if (html[at] !== "<") {
      let stop = html.indexOf("<", at);
      if (stop < 0) stop = html.length;
      const text = html.slice(at, stop);
      at = stop;
      if (TEXT_BAD.test(text) || decodeEntities(text) === null) return false;
      if (text.trim() === "") continue;
      if (!ELEMENTS.get(parent)?.text) return false;
      continue;
    }
    const tag = readTag(html, at);
    if (!tag) return false;
    at = tag.end;
    const spec = ELEMENTS.get(tag.name);
    if (!spec) return false;
    if (tag.close) {
      if (stack.pop() !== tag.name) return false;
      continue;
    }
    const allowed =
      parent === "" ? ROOT_CHILDREN : ELEMENTS.get(parent)?.children;
    if (!allowed?.has(tag.name)) return false;
    // A link cannot contain a link (the parser would close the first one).
    if (tag.name === "a" && stack.includes("a")) return false;
    if (!attributesValid(tag.name, spec, tag.attrs)) return false;
    if (spec.children === null) continue;
    if (tag.selfClose || stack.length >= maxDepth) return false;
    stack.push(tag.name);
  }
  return stack.length === 0;
}
